/**
 * Centralised RBAC (role-based access control) helper for the frontend.
 *
 * The backend is the source of truth — every API endpoint enforces firm
 * scoping and role checks server-side. These helpers exist to:
 *
 *   1. Hide UI that the user has no permission to use.
 *   2. Prevent a user from reaching a sensitive page directly via URL
 *      (e.g. typing /accounting/general-ledger as a paralegal).
 *
 * Bypassing this layer is not a security issue — the backend will still
 * reject the request. The frontend gate is purely a UX guard.
 */

import { ROLES } from './roles';

const SUPERUSER = ROLES.SUPERUSER;
const PARTNER = ROLES.PARTNER;
const ACCOUNTANT = 'ACCOUNTANT';
const ASSOCIATE = ROLES.ASSOCIATE;
const PARALEGAL = ROLES.PARALEGAL;
const SUPPORT_STAFF = ROLES.SUPPORT_STAFF;
const BOARD_MEMBER = ROLES.BOARD_MEMBER;
const AUDITOR = 'AUDITOR';

const ALL_STAFF = [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF];

/**
 * tabId → list of roles that may access it.
 *
 * Every real sidebar nav item id has an explicit entry here (closed by
 * default - see canAccessTab below). This used to be a partial list where
 * an absent tabId meant "open to all staff", which let ACCOUNTANT and
 * BOARD_MEMBER (both allowlist-style roles in App.js's old per-role sidebar
 * arrays) silently gain access to anything not yet added here. The
 * consolidation that made this the single source of truth for both sidebar
 * and content-area gating (frontend/src/App.js's filterItem now calls
 * canAccessTab directly instead of maintaining its own per-role arrays)
 * required making every tab explicit first, precisely to avoid that
 * silent-widening trap.
 */
export const TAB_ACCESS = {
  // ── Practice management — open to every case-facing staff role ──
  dashboard:              [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF, BOARD_MEMBER],
  clients:                [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  cases:                  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  documents:              [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  clauseLibrary:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  tasks:                  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  taskTemplates:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  calendar:               [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  unlinkedInbox:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  emailRules:             [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  communicationLogs:      [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  timeExpense:            [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  activityLog:            [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  myPerformance:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  // [WF-B5] AUDITOR added: backend's auditor_allowed_views whitelist
  // (legal_ops/permissions.py) already covers ReviewCycleViewSet/
  // PerformanceGoalViewSet/PerformanceReviewViewSet for read-only access;
  // PerformanceReviews.js has no internal view-block of its own (only a
  // canManageCycles write-gate that already excludes AUDITOR), so this was
  // a pure "backend allows, frontend blocks" gap.
  performanceReviews:     [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF, AUDITOR],
  conveyancingDashboard:  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  properties:             [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  bankInstructionInbox:   [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  conveyancingBatches:    [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  conveyancingReports:    [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  // Integrations (Outlook/email) is open to every staff role today, same as
  // it was before this consolidation - flagged in
  // training-docs/_meta/permission-audit-findings.md as worth a deliberate
  // product decision, not changed here.
  integrations:           [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],

  // ── Accounting & finance ─ accountants + partners only ──
  chartOfAccounts:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  generalLedger:          [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  suppliers:              [SUPERUSER, PARTNER, ACCOUNTANT],
  creditors:              [SUPERUSER, PARTNER, ACCOUNTANT],
  pettyCash:              [SUPERUSER, PARTNER, ACCOUNTANT],
  accountingReview:       [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  firmExpenses:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankAccounts:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankReconciliation:     [SUPERUSER, PARTNER, ACCOUNTANT],
  vatReturns:             [SUPERUSER, PARTNER, ACCOUNTANT],
  trialBalance:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  balanceSheet:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  incomeStatement:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  cashFlow:               [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  accountingPeriods:      [SUPERUSER, PARTNER, ACCOUNTANT],
  trustAccounting:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],

  // ── Admin & sensitive settings ──
  manageAuditors:         [SUPERUSER],
  // ACCOUNTANT added 2026-07-24: the sidebar's old per-role array already
  // showed this tab to Accountants, and FirmSettingsView's backend write
  // guard already allows Partner or Accountant - this list was the stale
  // one, denying the tab after the nav link was already shown.
  firmSettings:           [SUPERUSER, PARTNER, ACCOUNTANT],
  payrollManagement:      [SUPERUSER, PARTNER, ACCOUNTANT],
  // [WF-B5] AUDITOR added — same reasoning as performanceReviews above;
  // TeamOverview.js has no internal role gate at all (purely a read-only
  // dashboard), so the backend whitelist was the only thing missing.
  teamOverview:           [SUPERUSER, PARTNER, BOARD_MEMBER, AUDITOR],
  // [WF-B4] ACCOUNTANT already has full backend CRUD on Department
  // (permissions.py's unconditional Accountant branch), identical to
  // Partner — this was the hidden-capability half of the drift pattern:
  // full write access with no UI path to reach it at all.
  departments:            [SUPERUSER, PARTNER, ACCOUNTANT],
  // ACCOUNTANT is limited to invoices/payments/expenses by an in-method
  // entity allow-list in ImportViewSet.import_csv. [H11]
  imports:                [SUPERUSER, PARTNER, PARALEGAL, SUPPORT_STAFF, ACCOUNTANT],
  security:               ALL_STAFF.concat(BOARD_MEMBER),

  // ── Reports — staff who can see financials ──
  // ASSOCIATE added 2026-07-24: same "sidebar showed it, this list denied
  // it" pattern as firmSettings above.
  reports:                [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, BOARD_MEMBER, AUDITOR],
  insightsReports:        [SUPERUSER, PARTNER, ACCOUNTANT, BOARD_MEMBER],

  // ── Dual-approval workflow — attorneys who can sign off on paralegal work ──
  pendingApprovals:       [SUPERUSER, PARTNER, ASSOCIATE],

  // ── Billing / collections ──
  billing:                [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],
  debtCollectionDashboard: [SUPERUSER, PARTNER, ACCOUNTANT],
  collectionsSettings:    [SUPERUSER, PARTNER, ACCOUNTANT],
  collectionsWorklist:    [SUPERUSER, PARTNER, ACCOUNTANT, SUPPORT_STAFF],
  // [WF-B9] PARALEGAL added: IsTariffEditor's backend role list
  // (legal_ops/permissions.py) and BillOfCostsManagement.js's own
  // canEditTariffs gate both already correctly included PARALEGAL — this
  // was the one instance the audit found of the component being kept in
  // sync while TAB_ACCESS itself drifted, making the correct component
  // code permanently unreachable for that role.
  billsOfCosts:           [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],
  // NOTE: ASSOCIATE and PARALEGAL were previously excluded here despite
  // ASSOCIATE already having `canManage` rights in ServiceProductManagement.js
  // — the tab itself was invisible to them (same "check all three gates
  // independently" pattern documented in CLAUDE.md for SUPPORT_STAFF/Imports.
  serviceProducts:        [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],

  // ── Compliance & audit ──
  // Partner Controls — Module Access (per-firm, per-junior-role module
  // overrides). Partners + superusers only. Backend write gate:
  // FirmModuleAccessView -> IsPartnerOrSuperuser.
  moduleAccess:           [SUPERUSER, PARTNER],

  auditManagement:        [SUPERUSER, PARTNER, AUDITOR],
  // [WF-B2] ASSOCIATE/PARALEGAL added: they already have real case-scoped
  // backend read+write on ComplianceItem, and ComplianceHub.js's own
  // canViewPage gate already includes both — TAB_ACCESS was the one gate
  // still excluding them.
  complianceHub:          [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],
  // [WF-B1] SUPPORT_STAFF/ASSOCIATE added: StaffProfileViewSet.get_queryset()
  // already grants SUPPORT_STAFF full firm-wide access (same can_manage_all
  // bucket as Partner/Accountant), and ASSOCIATE already has a genuine
  // self-view path (AttorneyRecords.js's own canView, backend's
  // qs.filter(pk=staff_profile.pk) fallback) — TAB_ACCESS was blocking both
  // before either gate was ever evaluated.
  attorneyRecords:        [SUPERUSER, PARTNER, ACCOUNTANT, SUPPORT_STAFF, ASSOCIATE],
  annualCertificateManagement: [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
};

/**
 * tabId -> module key for the tabs governed by a firm's Module Access Control
 * overrides. Mirrors legal_ops/module_access.py MODULE_REGISTRY's tab_ids.
 * A tab not listed here is never affected by an override — it uses the plain
 * TAB_ACCESS role check only. tests_module_access.py asserts this stays in
 * sync with the backend registry.
 */
export const MODULE_TAB_MAP = {
  billing: 'invoicing',
  serviceProducts: 'serviceCatalog',
  billsOfCosts: 'billsOfCosts',
};

/**
 * Returns true if the user role can access the given tab.
 *
 * Superusers always get access. Unknown tabs fail closed (denied) - every
 * real tab id is listed above; an unlisted id means either a typo or a new
 * tab that hasn't been added to TAB_ACCESS yet, and the safe default for
 * either case is to hide it, not show it.
 *
 * `moduleAccess` is the resolved `{module_key: bool}` map the backend returns
 * on auth/current-user/ (and login). When a tab belongs to a module that the
 * caller's firm has turned off for their role, it's denied even though the
 * static TAB_ACCESS role list would allow it. Passing `null` (not yet loaded)
 * falls back to the role check alone — the backend still 403s the data, so the
 * worst case is a nav item that briefly appears then errors on click.
 */
export function canAccessTab(userRole, tabId, currentUser = null, moduleAccess = null) {
  if (currentUser && currentUser.is_superuser) return true;
  if (userRole === SUPERUSER) return true;
  const allowed = TAB_ACCESS[tabId];
  if (!allowed) return false; // Tab not gated - fail closed
  if (!allowed.includes(userRole)) return false;
  const moduleKey = MODULE_TAB_MAP[tabId];
  if (moduleKey && moduleAccess && moduleAccess[moduleKey] === false) return false;
  return true;
}

/**
 * Get the list of tabs the current user is NOT allowed to access.
 * Useful for redirecting away from blocked URLs.
 */
export function blockedTabsFor(userRole, currentUser = null, moduleAccess = null) {
  if (currentUser && currentUser.is_superuser) return [];
  if (userRole === SUPERUSER) return [];
  return Object.keys(TAB_ACCESS).filter(t => !canAccessTab(userRole, t, currentUser, moduleAccess));
}
